import React from "react";
import { Typography, Box } from "@mui/material";
import SimplePaper from "../../../components/SimplePaper";
import { MathComponent } from "mathjax-react";

const D9fromD1 = () => {
  const title = "Calculating D9 (Navamsa) from D1 (Rasi)";

  const body = (
    <>
      <Typography variant="body1" paragraph>
        The D9 chart, or Navamsa chart, is a significant divisional chart in
        Vedic astrology. It provides insights into relationships, spiritual
        growth, and the hidden strength of planets. Below is a step-by-step
        procedure to calculate the D9 chart from the D1 chart.
      </Typography>

      <Typography variant="h4" gutterBottom>
        Procedure to Calculate D9 (Navamsa) from D1 (Rasi)
      </Typography>

      <Typography variant="h5" gutterBottom>
        1. Identify the Planet's Degree in the D1 Chart
      </Typography>

      <Typography variant="body1" paragraph>
        First, note the degree of the planet in the D1 chart. For example, if
        Mars is located at 14° Aries, this degree will be used for the
        calculation.
      </Typography>

      <Typography variant="h5" gutterBottom>
        2. Divide the Degree by 3.33 to Find the Navamsa Position
      </Typography>

      <Typography variant="body1" paragraph>
        Each zodiac sign is divided into 9 equal parts, called Navamsas, with
        each Navamsa spanning 3°20' (or 3.33 degrees). To find which Navamsa a
        planet occupies, we divide the planet's degree within the sign by 3.33.
      </Typography>

      <Box sx={{ textAlign: "center", margin: "1rem 0" }}>
        <MathComponent
          tex={String.raw`Navamsa \ Position = \frac{Degree \ in \ Sign}{3.33}`}
        />
      </Box>

      <Typography variant="h5" gutterBottom>
        3. Determine the Integer Part
      </Typography>

      <Typography variant="body1" paragraph>
        The result from the division gives us the Navamsa position. Ignore the
        decimal part and take the integer value. For instance, if the result is
        4.2, we take 4 as the Navamsa position.
      </Typography>

      <Typography variant="h5" gutterBottom>
        4. Assign the Navamsa Sign
      </Typography>

      <Typography variant="body1" paragraph>
        The 9 Navamsas within each sign follow a specific sequence based on the
        element of the sign (Fire, Earth, Air, or Water).
      </Typography>

      <Box sx={{ margin: "1rem 0" }}>
        <Typography variant="h6" gutterBottom>
          - Fire Signs : Aries, Leo, Sagittarius
        </Typography>
        <Typography variant="body2" paragraph>
          Navamsa Sequence: Aries, Taurus, Gemini, Cancer, Leo, Virgo, Libra,
          Scorpio, Sagittarius.
        </Typography>

        <Typography variant="h6" gutterBottom>
          - Earth Signs : Taurus, Virgo, Capricorn
        </Typography>
        <Typography variant="body2" paragraph>
          Navamsa Sequence: Capricorn, Aquarius, Pisces, Aries, Taurus, Gemini,
          Cancer, Leo, Virgo.
        </Typography>

        <Typography variant="h6" gutterBottom>
          - Air Signs : Gemini, Libra, Aquarius
        </Typography>
        <Typography variant="body2" paragraph>
          Navamsa Sequence: Libra, Scorpio, Sagittarius, Capricorn, Aquarius,
          Pisces, Aries, Taurus, Gemini.
        </Typography>

        <Typography variant="h6" gutterBottom>
          - Water Signs : Cancer, Scorpio, Pisces
        </Typography>
        <Typography variant="body2" paragraph>
          Navamsa Sequence: Scorpio, Sagittarius, Capricorn, Aquarius, Pisces,
          Aries, Taurus, Gemini, Cancer.
        </Typography>
      </Box>

      <Typography variant="h4" gutterBottom>
        Example: Calculating Mars at 14° Aries
      </Typography>

      <Typography variant="h5" gutterBottom>
        1. Identify the Degree in the D1 Chart
      </Typography>

      <Typography variant="body1" paragraph>
        In this example, Mars is located at 14° Aries in the D1 chart.
      </Typography>

      <Typography variant="h5" gutterBottom>
        2. Divide the Degree by 3.33
      </Typography>

      <Typography variant="body1" paragraph>
        To calculate which Navamsa Mars occupies, divide 14° by 3.33:
      </Typography>

      <Box sx={{ textAlign: "center", margin: "1rem 0" }}>
        <MathComponent
          tex={String.raw`Navamsa \ Position = \frac{14°}{3.33}`}
        />
      </Box>

      <Box sx={{ textAlign: "center", margin: "1rem 0" }}>
        <MathComponent tex={String.raw`\approx 4.2`} />
      </Box>

      <Typography variant="h5" gutterBottom>
        3. Determine the Navamsa Position
      </Typography>

      <Typography variant="body1" paragraph>
        We take the integer part of the result, which is 4. This means Mars is
        in the 4th Navamsa of Aries.
      </Typography>

      <Typography variant="h5" gutterBottom>
        4. Assign the Navamsa Sign
      </Typography>

      <Typography variant="body1" paragraph>
        Since Aries is a Fire sign, the Navamsa sequence starts with Aries. The
        4th Navamsa corresponds to Cancer. Thus, Mars at 14° Aries in the D1
        chart is placed in Cancer in the D9 chart.
      </Typography>

      <Typography variant="h4" gutterBottom>
        Python Program to Calculate D9 (Navamsa) from D1
      </Typography>

      <Typography variant="body1" paragraph>
        Below is a Python program that performs the calculation of the D9
        (Navamsa) position from the D1 (Rasi) chart, using the planet's degree
        and sign in the D1 chart.
      </Typography>

      <Box
        component="pre"
        sx={{
          padding: "1rem",
          backgroundColor: "#f5f5f5",
          borderRadius: "5px",
        }}
      >
        <code>
          {`def calculate_navamsa(degree, sign):
    fire_signs = ['Aries', 'Leo', 'Sagittarius']
    earth_signs = ['Taurus', 'Virgo', 'Capricorn']
    air_signs = ['Gemini', 'Libra', 'Aquarius']
    water_signs = ['Cancer', 'Scorpio', 'Pisces']

    fire_sequence = ['Aries', 'Taurus', 'Gemini', 'Cancer', 'Leo', 'Virgo', 'Libra', 'Scorpio', 'Sagittarius']
    earth_sequence = ['Capricorn', 'Aquarius', 'Pisces', 'Aries', 'Taurus', 'Gemini', 'Cancer', 'Leo', 'Virgo']
    air_sequence = ['Libra', 'Scorpio', 'Sagittarius', 'Capricorn', 'Aquarius', 'Pisces', 'Aries', 'Taurus', 'Gemini']
    water_sequence = ['Scorpio', 'Sagittarius', 'Capricorn', 'Aquarius', 'Pisces', 'Aries', 'Taurus', 'Gemini', 'Cancer']

    navamsa_position = degree / 3.33
    navamsa_index = int(navamsa_position)

    if sign in fire_signs:
        navamsa_sign = fire_sequence[navamsa_index]
    elif sign in earth_signs:
        navamsa_sign = earth_sequence[navamsa_index]
    elif sign in air_signs:
        navamsa_sign = air_sequence[navamsa_index]
    elif sign in water_signs:
        navamsa_sign = water_sequence[navamsa_index]
    else:
        return "Invalid Sign"

    return navamsa_sign

# Example usage
planet = "Mars"
degree = 14
sign = "Aries"

navamsa_sign = calculate_navamsa(degree, sign)
print(f"{planet} at {degree}° {sign} in the D1 chart is placed in {navamsa_sign} in the D9 chart.")`}
        </code>
      </Box>

      <Typography variant="body1" paragraph>
        The above Python program uses the planet's degree in the D1 chart and
        the sign it occupies to calculate the Navamsa sign. It follows the same
        steps we have already covered manually.
      </Typography>

      <Typography variant="h4" gutterBottom>
        Explanation of the Formula Used in the Python Program
      </Typography>

      <Typography variant="body1" paragraph>
        The program divides the planet's degree by 3.33 to find the Navamsa
        position. It then takes the integer part of the result to identify which
        Navamsa the planet falls into. Based on the element of the sign (Fire,
        Earth, Air, Water), the correct Navamsa sequence is chosen, and the
        Navamsa sign is returned.
      </Typography>

      <Typography variant="body1" paragraph>
        This method programmatically reproduces the manual calculation and can
        be easily adjusted for different planets, degrees, and signs.
      </Typography>
    </>
  );

  return <SimplePaper title={title} body={body} />;
};

export default D9fromD1;
